<main>
  <loading-overlay [isLoading]="isLoading() > 0 || !enumsReady()" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'ApDocuments_Title' | translateWF: 'AP Documents' }}</h1>
        <div class="page__title_buttons">
          <p-speedDial [model]="speedDialItems" direction="left" [transitionDelay]="80">
            <ng-template #button let-toggleCallback="toggleCallback">
              <p-button (click)="toggleCallback($event)" [label]="'New' | translateWF: 'New'" icon="pi pi-plus" />
            </ng-template>
            <ng-template #item let-item let-toggleCallback="toggleCallback">
              <p-button variant="outlined" severity="secondary" (click)="toggleCallback($event, item)" [label]="item.label" [icon]="item.icon" />
            </ng-template>
          </p-speedDial>
        </div>
      </div>
    </div>

    @if (summary(); as s) {
      <div class="cs-summary-grid">
        <p-card data-variant="summary" [header]="'ApDocuments_OpenPayable' | translateWF: 'Open Payable'">
          <ng-template pTemplate="subtitle">{{ s.openPayable | number: '1.2-2' }} {{ currencyLabel(s.functionalCurrency) }}</ng-template>
          {{ s.postedCount }} {{ 'ApDocuments_Posted' | translateWF: 'posted' }}
        </p-card>
        <p-card data-variant="summary" [header]="'ApDocuments_DueThisWeek' | translateWF: 'Due This Week'">
          <ng-template pTemplate="subtitle">{{ s.dueThisWeek | number: '1.2-2' }} {{ currencyLabel(s.functionalCurrency) }}</ng-template>
          {{ s.dueThisWeekCount }} {{ 'ApDocuments_Documents' | translateWF: 'documents' }}
        </p-card>
        <p-card data-variant="summary" [header]="'ApDocuments_Drafts' | translateWF: 'Drafts'">
          <ng-template pTemplate="subtitle">{{ s.draftCount }}</ng-template>
          {{ 'ApDocuments_PendingPost' | translateWF: 'pending post' }}
        </p-card>
        <p-card data-variant="summary" data-severity="danger" [header]="'ApDocuments_Overdue' | translateWF: 'Overdue'">
          <ng-template pTemplate="subtitle">{{ s.overdueCount }}</ng-template>
          {{ 'ApDocuments_PastDue' | translateWF: 'past due' }}
        </p-card>
      </div>
    }

    <div class="cs-page-list-grid">
      @if (filterConfig() && showFilters()) {
        <div>
          <csl-dynamic-filters [filterConfig]="filterConfig()!" [state]="filterState" />
        </div>
      }

      <p-panel [header]="'ApDocuments_Title' | translateWF: 'AP Documents'">
        <ng-template pTemplate="icons">
          <p-button
            [label]="(showFilters() ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters() ? 'Hide Filters' : 'Show Filters')"
            [icon]="showFilters() ? 'pi pi-filter-slash' : 'pi pi-filter'"
            (click)="showFilters.set(!showFilters())"
            variant="outlined"
            size="small"
          />
        </ng-template>
        <p-card data-variant="compact">
          <p-table [value]="documents()" selectionMode="single" [lazy]="true" [sortField]="sortField()" [sortOrder]="sortOrder()" (onSort)="onSort($event)">
            <ng-template pTemplate="header">
              <tr>
                <!-- #5230 — sorting is server-side (the table holds one page), so a column is sortable only
                     where the document itself stores the field. Provider is deliberately NOT sortable: the
                     name lives on the Business master and the document keeps only a reference, so the query
                     has nothing to order by. Type / Total / Balance / Status likewise. -->
                <th pSortableColumn="number">{{ 'ApDocument_Identity' | translateWF: 'Fiscal / Document #' }}<p-sortIcon field="number" /></th>
                <th>{{ 'Type' | translateWF: 'Type' }}</th>
                <th>{{ 'ApDocument_Provider' | translateWF: 'Provider' }}</th>
                <th pSortableColumn="documentDate">{{ 'ApDocument_DocumentDate' | translateWF: 'Doc date' }}<p-sortIcon field="documentDate" /></th>
                <th pSortableColumn="dueDate">{{ 'ApDocument_DueDate' | translateWF: 'Due date' }}<p-sortIcon field="dueDate" /></th>
                <th pSortableColumn="modifiedDate">{{ 'ApDocument_ModifiedDate' | translateWF: 'Last modified' }}<p-sortIcon field="modifiedDate" /></th>
                <th class="text-right">{{ 'ApDocument_Total' | translateWF: 'Total' }}</th>
                <th class="text-right">{{ 'ApDocument_BalanceDue' | translateWF: 'Balance due' }}</th>
                <th>{{ 'Status' | translateWF: 'Status' }}</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-rowData>
              @let doc = getRowData(rowData);
              <tr>
                <td>
                  <!-- The provider's own printed number leads, our number opens the document — the pair a
                       clerk matches against paper, in one column. -->
                  <app-document-identity
                    [documentNumber]="doc.number"
                    [fiscalNumber]="doc.fiscalNumber"
                    [linkTitle]="'ApDocument_Open' | translateWF: 'Open this document'"
                    (documentClick)="redirectToDetails(doc.id ?? undefined)"
                  />
                  @if (doc.hasAdministrativePurchaseOrder) {
                    <p-tag
                      severity="warn"
                      icon="pi pi-lock"
                      [value]="'ApDocument_Administrative' | translateWF: 'Administrative PO'"
                      [pTooltip]="'ApDocument_Administrative_Tooltip' | translateWF: 'Draws on an Administrative purchase order; only role holders can open it.'"
                    />
                  }
                </td>
                <td>
                  {{ documentTypeLabel(doc.documentType) }}
                  @if (noteDetail(doc); as detail) {
                    <small class="block text-surface-500">{{ detail }}</small>
                  }
                </td>
                <td>{{ providerDisplay(doc) }}</td>
                <td>{{ doc.documentDate | date: 'dd/MM/yyyy' }}</td>
                <!-- An applied credit note owes nothing and falls due for nothing (#6324). -->
                <td>{{ creditNoteState(doc) === 'applied' ? '—' : (doc.dueDate | date: 'dd/MM/yyyy') }}</td>
                <!-- Never modified since creation reads as an em dash rather than a blank cell. -->
                <td>{{ (doc.modifiedDate | date: 'dd/MM/yyyy HH:mm') || '—' }}</td>
                <td class="text-right">{{ doc.totalAmount | number: '1.2-2' }} {{ currencyLabel(doc.currency) }}</td>
                <td class="text-right">{{ doc.balanceDue ?? doc.totalAmount | number: '1.2-2' }}</td>
                <td>
                  @switch (creditNoteState(doc)) {
                    @case ('applied') {
                      <p-tag severity="success" [value]="'ApDocument_CreditNote_Applied' | translateWF: 'Applied'" />
                    }
                    @case ('openCredit') {
                      <p-tag severity="info" [value]="'ApDocument_CreditNote_OpenCredit' | translateWF: 'Open credit'" />
                    }
                    @default {
                      <p-tag [severity]="statusSeverity(doc.status)" [value]="statusLabel(doc.status)" />
                    }
                  }
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="9">{{ 'ApDocuments_NotFound' | translateWF: 'No AP documents found' }}</td>
              </tr>
            </ng-template>
          </p-table>

          <p-paginator
            [rows]="pageSize()"
            [totalRecords]="totalRecords()"
            [first]="(currentPage() - 1) * pageSize()"
            (onPageChange)="onPageChange($event)"
            [rowsPerPageOptions]="[25, 50, 100]"
          />
        </p-card>
      </p-panel>
    </div>
  </div>
</main>
